// MS-L4B — Public legal page: Privacy Policy v1.0 (Pre-Beta).
// Content transcribed VERBATIM from the PM-approved DOCX
// (MASTRO_Privacy_Policy_v1.0_Pre-Beta). The internal "STATO DOCUMENTO"
// section is intentionally NOT rendered here per explicit instruction inside
// the source document itself ("La sezione «Stato documento» non fa parte del
// testo pubblico dell’informativa").
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { MastroBrand } from "@/components/MastroBrand";
import { LandingSection } from "@/components/landing/LandingSection";

const P = ({ children }) => (
  <p className="mt-3 text-[15px] leading-relaxed text-neutral-700">{children}</p>
);

const H2 = ({ n, children }) => (
  <h2 className="mt-10 text-lg font-bold tracking-tight text-neutral-950 sm:text-xl">
    {n}. {children}
  </h2>
);

export default function Privacy() {
  return (
    <div
      data-testid="privacy-page"
      className="min-h-screen w-full bg-white text-neutral-950 flex flex-col"
    >
      <LandingSection
        as="header"
        className="border-b border-neutral-100"
        innerClassName="flex items-center justify-between py-4"
      >
        <Link to="/" aria-label="Mastro — Home">
          <MastroBrand className="h-10" />
        </Link>
        <Link
          to="/"
          data-testid="privacy-back-home"
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-neutral-600 transition-colors hover:text-neutral-950"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna alla home
        </Link>
      </LandingSection>

      <main className="flex-1">
        <LandingSection innerClassName="py-12 sm:py-16">
          <article className="mx-auto w-full max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
              Mastro Privacy Policy
            </p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-neutral-950 sm:text-4xl">
              Informativa sul trattamento dei dati personali
            </h1>
            <p
              data-testid="privacy-version"
              className="mt-3 text-sm text-neutral-500"
            >
              Versione 1.0 — Pre-Beta · Ultimo aggiornamento: 4 settembre 2026
            </p>

            <div className="mt-8 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-700 sm:p-5">
              <p><span className="font-semibold text-neutral-900">Titolare:</span> Riccardo Gerri</p>
              <p className="mt-1">
                <span className="font-semibold text-neutral-900">Contatto privacy:</span>{" "}
                <a
                  href="mailto:privacy@iltuomastroapp.it"
                  className="text-teal-700 underline-offset-4 hover:underline"
                >
                  privacy@iltuomastroapp.it
                </a>
              </p>
            </div>

            <section className="mt-10">
              <h2 className="text-lg font-bold tracking-tight text-neutral-950 sm:text-xl">
                Indice
              </h2>
              <ol className="mt-3 list-decimal space-y-1 pl-5 text-[15px] text-neutral-700">
                <li>Chi gestisce Mastro</li>
                <li>Che cos’è Mastro</li>
                <li>Quali dati trattiamo</li>
                <li>Perché utilizziamo i dati</li>
                <li>Utilizzo dell’intelligenza artificiale</li>
                <li>Dati relativi ai tuoi clienti o ad altre persone</li>
                <li>Con chi possono essere condivisi i dati</li>
                <li>Trasferimenti internazionali</li>
                <li>Per quanto tempo conserviamo i dati</li>
                <li>Sicurezza dell’account</li>
                <li>Cookie e strumenti tecnici</li>
                <li>I tuoi diritti</li>
                <li>Dati che scegli di non fornire</li>
                <li>Modifiche alla Privacy Policy</li>
                <li>Contatti</li>
              </ol>
            </section>

            <section>
              <h2 className="mt-12 text-xl font-bold tracking-tight text-neutral-950 sm:text-2xl">
                Informativa
              </h2>

              <H2 n={1}>Chi gestisce Mastro</H2>
              <P>
                Mastro è un servizio digitale gestito da Riccardo Gerri. Per
                qualsiasi domanda relativa alla privacy o al trattamento dei
                dati personali puoi scrivere a:
              </P>
              <P>
                <a
                  href="mailto:privacy@iltuomastroapp.it"
                  className="text-teal-700 underline-offset-4 hover:underline"
                >
                  privacy@iltuomastroapp.it
                </a>
              </P>
              <P>
                Riccardo Gerri è il titolare del trattamento per i dati
                personali trattati per la gestione dell’account Mastro,
                l’erogazione del servizio, la sicurezza e gli altri
                trattamenti descritti nella presente informativa.
              </P>

              <H2 n={2}>Che cos’è Mastro</H2>
              <P>
                Mastro è un assistente digitale pensato per aiutare artigiani e
                professionisti a organizzare la memoria operativa del proprio
                lavoro. Il servizio può essere utilizzato principalmente
                attraverso WhatsApp e tramite la relativa area web. L’utente
                può inviare a Mastro messaggi, informazioni sui lavori,
                fotografie, note, misure, aggiornamenti e messaggi vocali.
                Mastro organizza queste informazioni e permette di ritrovarle
                successivamente nella propria area personale. Durante la fase
                Pre-Beta e Beta il servizio è fornito gratuitamente.
              </P>

              <H2 n={3}>Quali dati trattiamo</H2>
              <P>
                Quando utilizzi Mastro possiamo trattare i dati necessari per
                creare e gestire il tuo account, come numero di telefono
                WhatsApp, professione, indirizzo email eventualmente fornito
                per il recupero dell’account e informazioni relative
                all’attività professionale. Trattiamo inoltre le informazioni
                che decidi di affidare a Mastro durante il normale utilizzo
                del servizio. Possono comprendere dati relativi a lavori e
                clienti, descrizioni, appunti, aggiornamenti, misure,
                quantità, fotografie e relative didascalie, extra o
                variazioni, preventivi e altre informazioni professionali.
              </P>
              <P>
                I messaggi inviati attraverso WhatsApp vengono elaborati per
                comprendere la richiesta e svolgere le funzioni di Mastro.
                Quando invii un messaggio vocale, l’audio viene elaborato
                tramite un servizio di trascrizione automatica. Il file audio
                non viene conservato da Mastro come contenuto permanente del
                lavoro; può invece essere conservato il testo ottenuto dalla
                trascrizione.
              </P>
              <P>
                Le fotografie vengono conservate per permetterne l’associazione
                e la consultazione all’interno dei lavori. Nell’attuale
                architettura di Mastro le immagini non vengono inviate al
                modello di intelligenza artificiale utilizzato per comprendere
                i messaggi testuali.
              </P>
              <P>
                Possono inoltre essere trattati dati tecnici necessari al
                funzionamento e alla sicurezza del servizio, come informazioni
                relative alla sessione, autenticazione, log tecnici, browser,
                dispositivo, indirizzo IP e informazioni generate
                dall’infrastruttura di rete e hosting.
              </P>

              <H2 n={4}>Perché utilizziamo i dati</H2>
              <P>
                I dati vengono trattati principalmente per permetterti di
                utilizzare Mastro: creare e proteggere il tuo account,
                ricevere e organizzare le informazioni inviate, associare
                contenuti ai lavori corretti, consultare la tua memoria
                operativa, utilizzare la dashboard e generare le funzioni
                richieste. La base giuridica principale di questi trattamenti
                è l’esecuzione del rapporto contrattuale e delle funzionalità
                richieste dall’utente, ai sensi dell’articolo 6, paragrafo 1,
                lettera b) del GDPR.
              </P>
              <P>
                Alcuni dati possono inoltre essere trattati per proteggere
                Mastro, prevenire abusi, diagnosticare problemi tecnici e
                garantire la sicurezza del servizio. Tali attività possono
                fondarsi sul legittimo interesse del titolare a proteggere il
                servizio e i suoi utenti.
              </P>
              <P>
                Quando necessario, i dati possono infine essere trattati per
                adempiere a obblighi previsti dalla legge.
              </P>
              <P>
                Mastro non utilizza attualmente i dati degli utenti per
                pubblicità comportamentale o profilazione commerciale.
              </P>

              <H2 n={5}>Utilizzo dell’intelligenza artificiale</H2>
              <P>
                Mastro utilizza sistemi di intelligenza artificiale per
                comprendere il significato dei messaggi e fornire le
                funzionalità richieste dall’utente. Questo significa che il
                testo inviato dall’utente e le informazioni di contesto
                necessarie a comprendere la richiesta possono essere elaborati
                tramite servizi di intelligenza artificiale. I messaggi
                vocali possono essere inviati a un servizio specializzato di
                riconoscimento vocale per essere convertiti in testo. Mastro
                utilizza attualmente servizi forniti da OpenAI per alcune
                funzioni di intelligenza artificiale e trascrizione vocale.
                L’intelligenza artificiale viene utilizzata come strumento di
                interpretazione e assistenza. Mastro non utilizza sistemi
                automatizzati per prendere decisioni che producano effetti
                giuridici o effetti analogamente significativi sull’utente.
                Le operazioni rilevanti all’interno del servizio possono
                inoltre richiedere conferma da parte dell’utente prima di
                essere eseguite.
              </P>

              <H2 n={6}>Dati relativi ai tuoi clienti o ad altre persone</H2>
              <P>
                Durante l’utilizzo di Mastro potresti inserire informazioni
                relative ai tuoi clienti, collaboratori o altre persone, ad
                esempio nomi, fotografie, indirizzi di intervento, descrizioni
                di lavori o informazioni contenute nelle tue note. Se
                inserisci dati personali relativi ad altre persone, devi
                assicurarti di avere titolo per raccoglierli e utilizzarli
                nell’ambito della tua attività professionale. Quando tali dati
                sono trattati nell’ambito della tua attività professionale,
                sei responsabile di determinarne finalità e base giuridica.
                Mastro tratta queste informazioni per fornire le funzionalità
                richieste dall’utente e non le utilizza autonomamente per
                finalità commerciali proprie. Il rapporto relativo al
                trattamento di dati per conto dell’utente sarà disciplinato
                anche dalle Condizioni di utilizzo di Mastro e, ove
                applicabile, da disposizioni sul trattamento per conto del
                titolare ai sensi dell’articolo 28 GDPR.
              </P>

              <H2 n={7}>Con chi possono essere condivisi i dati</H2>
              <P>
                Per funzionare, Mastro utilizza alcuni fornitori tecnici. A
                seconda della funzione utilizzata, i dati possono essere
                trattati da fornitori di servizi di messaggistica,
                intelligenza artificiale e trascrizione vocale, hosting e
                infrastruttura cloud, database e archiviazione di file,
                sicurezza e distribuzione del servizio, nonché posta
                elettronica utilizzata per assistenza e gestione delle
                richieste privacy. In particolare, l’utilizzo di Mastro
                tramite WhatsApp comporta il trattamento delle comunicazioni
                attraverso i servizi Meta/WhatsApp. L’utilizzo di WhatsApp
                resta inoltre soggetto ai termini e alle informative di
                Meta/WhatsApp per i trattamenti svolti autonomamente da tali
                soggetti. Mastro non vende i dati personali degli utenti. I
                dati possono inoltre essere comunicati ad autorità pubbliche o
                altri soggetti quando ciò sia richiesto dalla legge.
              </P>

              <H2 n={8}>Trasferimenti internazionali</H2>
              <P>
                Alcuni fornitori tecnici utilizzati per erogare Mastro possono
                comportare il trattamento di dati anche al di fuori dello
                Spazio Economico Europeo. Quando applicabile, tali
                trasferimenti sono gestiti mediante gli strumenti e le
                garanzie previsti dalla normativa europea in materia di
                protezione dei dati, come decisioni di adeguatezza o garanzie
                contrattuali idonee. È possibile richiedere ulteriori
                informazioni sui fornitori utilizzati e sulle garanzie
                applicabili scrivendo a{" "}
                <a
                  href="mailto:privacy@iltuomastroapp.it"
                  className="text-teal-700 underline-offset-4 hover:underline"
                >
                  privacy@iltuomastroapp.it
                </a>
                .
              </P>

              <H2 n={9}>Per quanto tempo conserviamo i dati</H2>
              <P>
                I dati dell’account e dei lavori vengono conservati finché
                l’account Mastro rimane attivo, salvo che sia necessario
                conservarli ulteriormente per obblighi di legge o per la
                tutela di diritti. Durante la Pre-Beta, se l’utente richiede
                la cancellazione dell’account, la cancellazione viene gestita
                manualmente. A seguito della richiesta, i dati vengono
                eliminati dai sistemi attivi compatibilmente con i tempi
                tecnici necessari e con eventuali obblighi di legge.
                Eventuali copie presenti nei sistemi di backup possono
                permanere temporaneamente fino alla normale rotazione dei
                backup e non vengono utilizzate per il normale funzionamento
                del servizio. L’archiviazione o la rimozione di un lavoro o
                di un preventivo dalla normale visualizzazione
                dell’applicazione non deve essere considerata equivalente
                alla cancellazione dei dati personali dell’account.
              </P>

              <H2 n={10}>Sicurezza dell’account</H2>
              <P>
                Mastro utilizza misure tecniche volte a proteggere l’accesso
                agli account e a mantenere separati i dati appartenenti a
                utenti diversi. Le password non vengono conservate nel sistema
                in forma leggibile. L’accesso alla dashboard utilizza sistemi
                di autenticazione e sessione e i dati di ciascun workspace
                sono gestiti separatamente da quelli degli altri utenti.
                Nessun sistema informatico può tuttavia garantire una
                sicurezza assoluta.
              </P>

              <H2 n={11}>Cookie e strumenti tecnici</H2>
              <P>
                Mastro utilizza strumenti tecnici necessari al funzionamento
                dell’applicazione, ad esempio per mantenere la sessione
                autenticata e ricordare alcune preferenze del dispositivo.
                Nell’attuale configurazione Pre-Beta, il codice applicativo
                di Mastro non utilizza PostHog, session recording, cookie
                pubblicitari o strumenti di profilazione commerciale.
                L’infrastruttura tecnica utilizzata per distribuire e
                proteggere il servizio può utilizzare cookie o strumenti
                tecnici propri necessari per sicurezza, protezione da abusi,
                gestione delle sessioni di rete e funzionamento del servizio.
                Se in futuro verranno introdotti strumenti non strettamente
                necessari, Mastro aggiornerà le informazioni fornite agli
                utenti e, quando richiesto, raccoglierà preventivamente le
                relative scelte di consenso.
              </P>

              <H2 n={12}>I tuoi diritti</H2>
              <P>
                Nei casi previsti dal GDPR puoi chiedere: accesso ai tuoi dati
                personali; rettifica dei dati inesatti; cancellazione dei
                dati; limitazione del trattamento; opposizione al trattamento;
                portabilità dei dati, quando applicabile. Le richieste possono
                essere inviate a:
              </P>
              <P>
                <a
                  href="mailto:privacy@iltuomastroapp.it"
                  className="text-teal-700 underline-offset-4 hover:underline"
                >
                  privacy@iltuomastroapp.it
                </a>
              </P>
              <P>
                Risponderemo senza ingiustificato ritardo e comunque entro i
                termini previsti dal GDPR. Il termine ordinario è un mese dal
                ricevimento della richiesta e può essere prorogato nei casi
                consentiti dalla normativa. Durante la Pre-Beta le richieste
                di accesso, esportazione, rettifica e cancellazione possono
                essere gestite manualmente. Hai inoltre il diritto di proporre
                reclamo all’autorità di controllo competente. In Italia è il
                Garante per la protezione dei dati personali.
              </P>

              <H2 n={13}>Dati che scegli di non fornire</H2>
              <P>
                Alcuni dati sono necessari per utilizzare determinate
                funzionalità di Mastro. Ad esempio, senza un numero WhatsApp
                non è possibile utilizzare il servizio attraverso WhatsApp e
                senza le informazioni relative a un lavoro Mastro non può
                organizzare quel lavoro. Non sei invece obbligato a comunicare
                informazioni ulteriori rispetto a quelle necessarie alla
                funzione che desideri utilizzare. Ti invitiamo in particolare
                a non inserire in Mastro categorie particolarmente delicate
                di dati personali quando non siano realmente necessarie per
                la tua attività.
              </P>

              <H2 n={14}>Modifiche alla Privacy Policy</H2>
              <P>
                Mastro è attualmente in fase Pre-Beta e il servizio può
                evolvere. Questa Privacy Policy potrà essere aggiornata quando
                cambieranno le funzionalità, i fornitori utilizzati o le
                modalità di trattamento dei dati. In caso di modifiche
                rilevanti verrà aggiornata la data indicata all’inizio della
                presente informativa e, quando opportuno, gli utenti saranno
                informati attraverso il servizio.
              </P>

              <H2 n={15}>Contatti</H2>
              <P>
                Per qualsiasi richiesta relativa ai tuoi dati personali o alla
                presente Privacy Policy:
              </P>
              <P>Riccardo Gerri</P>
              <P>Gestore di Mastro</P>
              <P>
                <a
                  href="mailto:privacy@iltuomastroapp.it"
                  className="text-teal-700 underline-offset-4 hover:underline"
                >
                  privacy@iltuomastroapp.it
                </a>
              </P>
              <P>iltuomastroapp.it</P>

              <p className="mt-10 text-sm italic text-neutral-500">
                Fine del testo dell’informativa.
              </p>
            </section>
          </article>
        </LandingSection>
      </main>

      <LandingSection
        as="footer"
        className="border-t border-neutral-100"
        innerClassName="flex flex-col gap-2 py-6 text-xs text-neutral-400 sm:flex-row sm:items-center sm:justify-between"
      >
        <span>Mastro — la memoria operativa del professionista.</span>
        <div className="flex items-center gap-4">
          <Link
            to="/privacy"
            className="text-neutral-500 hover:text-neutral-700"
          >
            Privacy Policy
          </Link>
          <Link
            to="/termini"
            className="text-neutral-500 hover:text-neutral-700"
          >
            Termini
          </Link>
        </div>
      </LandingSection>
    </div>
  );
}
